import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Camera, Video, RefreshCw } from "lucide-react";

/**
 * PhoneCameraCast — the page a phone loads via QR pair link.
 *
 * Requests camera + mic, creates a WebRTC PeerConnection, and pushes video
 * to the host's Director Console over the /api/phone-camera/ws/{token}
 * signaling channel.  Uses the environment's REACT_APP_BACKEND_URL.
 *
 * No login required — owning the pairing token IS the auth.
 */
const STUN = [{ urls: "stun:stun.l.google.com:19302" }];

export default function PhoneCameraCast() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | joining | live | ended
  const [facing, setFacing] = useState("environment");
  const videoRef = useRef(null);
  const pcRef = useRef(null);
  const wsRef = useRef(null);
  const streamRef = useRef(null);

  // Verify token
  useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    fetch(`${base}/api/phone-camera/pair/${encodeURIComponent(token)}`)
      .then(async (r) => {
        if (!r.ok) throw new Error(`Invalid pair token (${r.status})`);
        return r.json();
      })
      .then(setInfo)
      .catch((e) => setErr(e.message || String(e)));
  }, [token]);

  const join = async () => {
    setStatus("joining"); setErr(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: facing }, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: { echoCancellation: true, noiseSuppression: true },
      });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;

      const pc = new RTCPeerConnection({ iceServers: STUN });
      pcRef.current = pc;
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));

      const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");
      const ws = new WebSocket(`${base}/api/phone-camera/ws/${encodeURIComponent(token)}?role=phone`);
      wsRef.current = ws;

      pc.onicecandidate = (e) => {
        if (e.candidate && ws.readyState === 1) ws.send(JSON.stringify({ type: "ice", candidate: e.candidate }));
      };
      pc.onconnectionstatechange = () => {
        if (pc.connectionState === "connected") setStatus("live");
        if (pc.connectionState === "failed" || pc.connectionState === "disconnected") setStatus("ended");
      };

      ws.onopen = async () => {
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        ws.send(JSON.stringify({ type: "offer", sdp: pc.localDescription }));
      };
      ws.onmessage = async (ev) => {
        try {
          const m = JSON.parse(ev.data);
          if (m.type === "answer" && m.sdp) {
            await pc.setRemoteDescription(new RTCSessionDescription(m.sdp));
          } else if (m.type === "ice" && m.candidate) {
            try { await pc.addIceCandidate(new RTCIceCandidate(m.candidate)); } catch {}
          } else if (m.type === "peer_left") {
            setStatus("ended");
          }
        } catch {}
      };
      ws.onerror = () => setErr("Signaling error");
      ws.onclose = () => { if (status === "live") setStatus("ended"); };
    } catch (e) {
      setErr(e.message || String(e));
      setStatus("idle");
    }
  };

  const stop = () => {
    try { wsRef.current?.close?.(); } catch {}
    try { pcRef.current?.close?.(); } catch {}
    try { streamRef.current?.getTracks?.()?.forEach((t) => t.stop()); } catch {}
    wsRef.current = null; pcRef.current = null; streamRef.current = null;
    setStatus("ended");
  };

  useEffect(() => () => stop(), []);

  const flip = async () => {
    const next = facing === "environment" ? "user" : "environment";
    setFacing(next);
    if (streamRef.current && pcRef.current) {
      try {
        const newStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: next } }, audio: false,
        });
        const newTrack = newStream.getVideoTracks()[0];
        const sender = pcRef.current.getSenders().find((s) => s.track && s.track.kind === "video");
        if (sender) await sender.replaceTrack(newTrack);
        streamRef.current.getVideoTracks().forEach((t) => t.stop());
        streamRef.current.removeTrack(streamRef.current.getVideoTracks()[0]);
        streamRef.current.addTrack(newTrack);
        if (videoRef.current) videoRef.current.srcObject = streamRef.current;
      } catch (e) { /* fall back: no-op */ }
    }
  };

  return (
    <div data-testid="phone-camera-cast" className="min-h-screen bg-black text-white flex flex-col">
      <header className="p-4 border-b border-white/10 flex items-center gap-3">
        <Camera className="w-5 h-5 text-tv-accent" />
        <div className="flex-1">
          <div className="font-black text-base"><span className="text-red-400">TAP IN</span> Camera</div>
          <div className="text-white/50 text-xs">{info?.host_display_name ? `Casting to ${info.host_display_name}` : "Pairing…"}</div>
        </div>
        {status === "live" && <span className="bg-red-500/20 text-red-200 border border-red-500/50 text-[10px] font-black px-2 py-1 rounded uppercase animate-pulse">● Live</span>}
      </header>

      <div className="flex-1 relative bg-black">
        <video ref={videoRef} playsInline autoPlay muted className="absolute inset-0 w-full h-full object-cover" />
        {!streamRef.current && !err && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center max-w-xs p-6">
              <Video className="w-12 h-12 text-tv-accent mx-auto mb-3" />
              <div className="font-black text-lg">Cast your camera</div>
              <div className="text-white/60 text-sm mt-1 mb-4">
                Tap "Start casting" to share this phone's camera + mic with {info?.host_display_name || "the host"}.
              </div>
              <button
                data-testid="phone-cam-start"
                onClick={join}
                disabled={!info || status === "joining"}
                className="bg-tv-accent hover:bg-yellow-400 disabled:opacity-40 text-black font-black px-4 py-2 rounded w-full"
              >
                {status === "joining" ? "Connecting…" : "Start casting"}
              </button>
            </div>
          </div>
        )}
        {err && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center max-w-xs p-6">
              <div className="font-black text-red-300 text-lg">Couldn't start</div>
              <div className="text-white/70 text-sm mt-1">{err}</div>
            </div>
          </div>
        )}
      </div>

      {streamRef.current && (
        <footer className="p-4 border-t border-white/10 flex items-center gap-3">
          <button onClick={flip} data-testid="phone-cam-flip" className="bg-white/10 hover:bg-white/20 text-white font-bold text-sm px-3 py-2 rounded inline-flex items-center gap-1.5">
            <RefreshCw className="w-4 h-4" /> Flip
          </button>
          <button onClick={stop} data-testid="phone-cam-stop" className="ml-auto bg-red-500 hover:bg-red-400 text-white font-black text-sm px-4 py-2 rounded">
            End cast
          </button>
        </footer>
      )}
    </div>
  );
}
